import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import cn from 'classnames'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import type {NavLinksGroupSliceV2} from 'prismicio-types'
import {useState} from 'react'
import {EVENTS, getLinkLocale, track} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {Icon} from 'src/common/Icon'
import type {Context} from 'src/common/types'

type NavLinksGroupVariationV2Props = {
  context: Context
  slice: NavLinksGroupSliceV2
  index: number
}

export const NavLinksGroupVariationV2 = ({context, slice, index: componentIndex}: NavLinksGroupVariationV2Props) => {
  const [isOpen, setIsOpen] = useState(slice.primary.opened_as_default || false)
  const domainCountry = useGetDomainCountry()
  const {linkResolver} = useLinkResolver()
  const {locale} = useRouter()

  const toggleOpen = () => {
    setIsOpen((prev) => !prev)
  }

  const onClick = (columnIndex: number, index: number, text: KeyTextField) => {
    track({
      event: EVENTS.CLICK,
      dataType: 'link',
      mktgId: `footer-link`,
      pageName: context.pageUid,
      location: `footer-link-${columnIndex}-${index}`,
    })

    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'footer-link',
        elementText: text || '',
        location: `footer-link-${columnIndex}-${index}`,
      },
      page: {
        domainCountry,
        type: context.pageUid,
      },
    })
  }

  return (
    <div
      className="flex flex-col bg-transparent max-lg:w-full max-lg:justify-center max-lg:py-6 lg:gap-8"
      onClick={toggleOpen}
    >
      <span className="text-button uppercase leading-[23px] text-green-neon max-lg:flex max-lg:items-center max-lg:justify-between">
        {slice.primary.title}
        <Icon
          name="chevron-v2"
          className={cn('h-1.5 w-2.5 text-green-neon transition-transform duration-300 lg:hidden', {
            'rotate-180': !isOpen,
          })}
        />
      </span>
      <ul
        className={cn(
          'flex flex-col gap-3 text-[14px] font-medium leading-[normal] tracking-[.14px] text-moss-white max-lg:overflow-hidden max-lg:transition-all max-lg:duration-300 max-lg:ease-in-out',
          {
            'max-lg:max-h-0 max-lg:opacity-0 max-lg:pt-0': !isOpen,
            'max-lg:max-h-[1000px] max-lg:opacity-100 max-lg:pt-3': isOpen,
          },
        )}
      >
        {slice.items.map((item, index) => {
          const linkLocale = getLinkLocale(item.link, locale)

          return (
            <li key={item.label! + index} onClick={() => onClick(componentIndex, index, item.label)}>
              <PrismicNextLink
                href={linkResolver(item.link)}
                locale={linkLocale}
                className="flex gap-2 text-[14px] font-medium leading-[normal] tracking-[.14px] text-moss-white hover:text-moss-white/[80%]"
                data-mktg-id={`category-${item.label?.toLowerCase()}`}
                data-type={`order-${componentIndex}`}
              >
                {item.label}
                {isFilled.select(item.tag) && (
                  <div className="flex min-h-4 w-fit items-center justify-center text-nowrap rounded-[4px] bg-moss-blue px-1.5 text-[8px] font-semibold uppercase tracking-[.48px] text-moss-white">
                    {item.tag}
                  </div>
                )}
              </PrismicNextLink>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
